import { getSupabaseFunctionErrorDetails } from "@/lib/supabase-function-errors";

export type AgenticFlowProductStatus =
  | "Building"
  | "Active"
  | "Paused"
  | "Completed"
  | "Failed"
  | "Archived";

export type AgenticFlowActivity = {
  id: string;
  actionVersion?: string | null;
  occurredAt: string;
  title: string;
  summary: string;
  effectSummary: string | null;
  state: "succeeded" | "failed" | "duplicate_suppressed" | "changed";
};

export type AgenticFlowExecutionRecord = {
  status: "started" | "succeeded" | "failed" | "outcome_unconfirmed";
  origin: "manual" | "test";
  startedAt: string;
  completedAt: string | null;
  resultSummary: string | null;
  failureSummary: string | null;
  effectOutcome: "none_allowed" | null;
  authority: AgenticFlowAuthority | null;
};

export type AgenticFlowProductItem = {
  actionVersion?: string | null;
  id: string;
  name: string;
  purpose: string;
  status: AgenticFlowProductStatus;
  progressLabel: string | null;
  trigger: string;
  scheduleDescription?: string | null;
  autonomy: string;
  lastRunAt: string | null;
  latestOutcome: string | null;
  nextRunAt: string | null;
  warning: string | null;
  canRunNow: boolean;
  canCheckSetup?: boolean;
  setupCheckState?: "checking" | "failed" | null;
  recurrence?: {
    state: string;
    tested: boolean;
    canTest: boolean;
    canEnable: boolean;
    canRecover?: boolean;
  } | null;
};

export type AgenticFlowProductDetail = AgenticFlowProductItem & {
  whatItSees: string;
  whatItCanDo: string;
  activities: AgenticFlowActivity[];
  executionRecords?: AgenticFlowExecutionRecord[];
  authority?: AgenticFlowAuthority | null;
};

export type AgenticFlowAuthority = {
  reads: string[];
  changes: string[];
  externalActions: string[] | null;
};

export type AgenticFlowDebugDetail = {
  logicalFlowId: string;
  revision: number;
  executionMode: string;
  providerAppIdentity: string | null;
  nativeAgentIdentity: string | null;
  workflowIdentity: string | null;
  functionIdentities: string[];
  lastValidationAt: string | null;
  lifecycle: string;
  discoveryId: string | null;
  discoveryCandidateId: string | null;
  usageObservations: unknown[];
};

export type AgenticFlowListResponse = {
  flows: AgenticFlowProductItem[];
  setupAttempts: AgenticFlowProductItem[];
  archivedCount: number;
};

export function executionDisplayText(value: string): string {
  return value
    .replace(/https?:\/\/\S+/gi, "an App link")
    .replace(/\b(?:WORKFLOW_LLM|NATIVE_AGENT|WORKFLOW_AGENT|base44_prompt)\b/gi, "App work")
    .replace(/\bAgentic Flows\b/gi, "execution plans")
    .replace(/\bAgentic Flow\b/gi, "execution plan")
    .replace(/\b(?:Base44|Provider)\b/gi, "the App service")
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, "an App item")
    .replace(/\b[0-9a-f]{64}\b/gi, "an internal reference");
}

export async function agenticFlowErrorMessage(error: unknown, fallback: string): Promise<string> {
  const details = await getSupabaseFunctionErrorDetails(error, fallback);
  if (details.status === 401 || /^unauthorized$/i.test(details.message)) {
    return "Your session needs to be refreshed. Reload Applyt and try again.";
  }
  if (details.status === 403 || /^forbidden$/i.test(details.message)) {
    return "You do not have permission to change this execution plan.";
  }
  return executionDisplayText(details.message);
}

export function agenticFlowStatusTone(status: AgenticFlowProductStatus): string {
  switch (status) {
    case "Active": return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "Building": return "border-sky-200 bg-sky-50 text-sky-700";
    case "Failed": return "border-rose-200 bg-rose-50 text-rose-700";
    case "Paused": return "border-amber-200 bg-amber-50 text-amber-800";
    case "Completed": return "border-violet-200 bg-violet-50 text-violet-700";
    case "Archived": return "border-slate-200 bg-slate-100 text-slate-600";
  }
}

export function formatAgenticFlowTime(value: string | null): string {
  if (!value) return "Not yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not yet";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}
